import React, { useEffect, useCallback } from "react";
import { Drawer, Flex, Button, Tag, message } from "antd";
import { useSelector, useDispatch } from "react-redux";
import OverviewS1 from "./OverviewS1";
import FormatsS2 from "./FormatsS2";
import {
  updateEntityOverviewAction,
  getSingleEntityDetailsAction,
  getEntityFormatsAction,
} from "../../../../../../../store/action/admin/entityActions";
import CustomLoaderIcon from "../../../../../../../Theme/CustomIcons/Icons/Essetional/CustomLoaderIcon";

const OverviewDrawerParent = ({
  open,
  onClose,
  title = "Overview",
  entityId,
}) => {
  const [currentStep, setCurrentStep] = React.useState(0);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const themeDefault = useSelector((state) => state.themeDefault);
  const singleEntityState = useSelector((state) => state.getSingleEntityList);
  const getEntityFormatsList = useSelector(
    (state) => state.getEntityFormatsList
  );
  const dispatch = useDispatch();
  const s1Ref = React.useRef(null);
  const s2Ref = React.useRef(null);

  const entityData = singleEntityState?.info || {};

  // Function to refresh entity data after updates
  const refreshEntityData = useCallback(async () => {
    if (entityData._id) {
      try {
        setIsRefreshing(true);
        await dispatch(getSingleEntityDetailsAction(entityData._id));
      } catch (error) {
        console.error("Failed to refresh entity data:", error);
      } finally {
        setIsRefreshing(false);
      }
    }
  }, [dispatch, entityData._id]);

  const populateOverviewForm = useCallback(() => {
    if (
      s1Ref.current?.setValues &&
      entityData &&
      Object.keys(entityData).length > 0
    ) {
      const overviewValues = {
        entityId: entityData.entityId || "",
        entityName: entityData.name || "",
        description: entityData.description || "",
        enableDimensions: entityData.dimensionsEnabled || false,
        enableRegionalPerDimension:
          entityData.enableRegionalPerDimension || false,
        country: entityData.country ?? undefined,
        currency: entityData.currency ?? undefined,
        timezone: entityData.timezone ?? undefined,
        language: entityData.language ?? undefined,
        enableFinancialConsolidation:
          entityData.useForFinancialConsolidation || false,
        enableEntityFilter: entityData.enableAsFilter || false,
        internalNotes: entityData.internalNotes || "",
      };
      s1Ref.current.setValues(overviewValues);
    }
  }, [entityData]);

  // Populate forms when drawer opens or entity data changes
  useEffect(() => {
    if (open && entityData && Object.keys(entityData).length > 0) {
      requestAnimationFrame(() => {
        populateOverviewForm();
      });
    }
  }, [open, entityData, populateOverviewForm]);

  // Load formats when drawer opens to check if FormatsS2 should be enabled
  useEffect(() => {
    if (open && entityId) {
      dispatch(getEntityFormatsAction(entityId));
    }
  }, [open, entityId, dispatch]);

  // Populate form when step changes
  useEffect(() => {
    if (entityData && Object.keys(entityData).length > 0) {
      requestAnimationFrame(() => {
        if (currentStep === 0) {
          // Reset OverviewS1 form and populate with fresh data
          if (s1Ref.current?.resetForm) {
            s1Ref.current.resetForm();
          }
          populateOverviewForm();
        } else if (currentStep === 1) {
          // Reset FormatsS2 form and load fresh data
          if (s2Ref.current?.resetForm) {
            s2Ref.current.resetForm();
          }
          // Load fresh formats data
          if (entityId) {
            dispatch(getEntityFormatsAction(entityId));
          }
        }
      });
    }
  }, [currentStep, entityData, populateOverviewForm, entityId, dispatch]);

  const steps = [
    { key: 0, title: "OVERVIEW" },
    { key: 1, title: "FORMATS" },
  ];

  const renderStepContent = () => {
    if (currentStep === 0)
      return <OverviewS1 ref={s1Ref} entityId={entityId} />;
    if (currentStep === 1) return <FormatsS2 ref={s2Ref} entityId={entityId} />;
    return null;
  };

  const StepTag = ({ step }) => {
    const isActive = currentStep >= step.key;
    const cardBorderColor =
      themeDefault?.info?.newConfig?.cardBorderColor || "#f0f0f0";
    const colorPrimary = themeDefault?.info?.colorPrimary || "#1890ff";
    const textColor = themeDefault?.info?.newConfig?.color;
    const radius = themeDefault?.info?.borderRadius;

    // Enable FormatsS2 navigation if formats exist or if it's the current step
    const hasFormats =
      getEntityFormatsList?.info &&
      Object.keys(getEntityFormatsList.info).length > 0;
    const isDisabled = step.key === 1 && !hasFormats && currentStep !== 1;

    return (
      <Tag
        onClick={isDisabled ? undefined : () => setCurrentStep(step.key)}
        style={{
          borderRadius: radius + "px",
          padding: "6px 10px",
          background: isActive ? colorPrimary + "30" : "transparent",
          color: isDisabled ? "#ccc" : textColor,
          cursor: isDisabled ? "not-allowed" : "pointer",
          border: `1px solid ${isActive ? colorPrimary : cardBorderColor}`,
          opacity: isDisabled ? 0.6 : 1,
        }}
      >
        <Flex gap={8} align="center">
          <span
            style={{
              width: themeDefault?.info?.newConfig?.fontMediumSize,
              height: themeDefault?.info?.newConfig?.fontMediumSize,
              borderRadius: "50%",
              background: isActive ? colorPrimary : cardBorderColor,
              color: isActive
                ? themeDefault?.info?.newConfig?.colorTextSecondary
                : colorPrimary,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "bold",
            }}
          >
            {step.key + 1}
          </span>
          {step.title}
        </Flex>
      </Tag>
    );
  };
  return (
    <Drawer
      title={title}
      placement="right"
      open={open}
      onClose={onClose}
      width="100%"
      destroyOnClose
      styles={{ body: { padding: 0 } }}
      footer={
        <Flex justify="space-between" align="center" style={{ width: "100%" }}>
          <Button onClick={onClose}>Cancel</Button>
          <Flex gap={8}>
            {/* <Button
              loading={isSubmitting}
              onClick={async () => {
                try {
                  setIsSubmitting(true);

                  if (currentStep === 0) {
                    const v = s1Ref.current?.getValues?.() || {};
                    const payload = {
                      entityId: v.entityId,
                      name: v.entityName,
                      description: v.description,
                      enableRegionalPerDimension: Boolean(
                        v.enableRegionalPerDimension
                      ),
                      dimensionsEnabled: Boolean(v.enableDimensions),
                      fileAttachments: [],
                      country: v.country,
                      currency: v.currency,
                      timezone: v.timezone,
                      language: v.language,
                      useForFinancialConsolidation: Boolean(
                        v.enableFinancialConsolidation
                      ),
                      enableAsFilter: Boolean(v.enableEntityFilter),
                      internalNotes: v.internalNotes,
                    };

                    // Call the update API
                    await dispatch(
                      updateEntityOverviewAction(entityData._id, payload)
                    );
                    message.success("Overview saved as draft!");

                    // Don't refresh - keep drawer open for draft saves
                  } else if (currentStep === 1) {
                    // Let FormatsS2 handle its own submission
                    await s2Ref.current?.submit?.();
                    message.success("Formats saved as draft!");

                    if (entityId) {
                      await dispatch(getEntityFormatsAction(entityId));
                    }
                  }
                } catch (e) {
                  console.error("Draft save failed:", e);
                  message.error(e || "Failed to save as draft");
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              Save as Draft
            </Button> */}
            <Button
              type="primary"
              loading={isSubmitting || isRefreshing}
              onClick={async () => {
                try {
                  setIsSubmitting(true);

                  if (currentStep === 0) {
                    const v = s1Ref.current?.getValues?.() || {};
                    const payload = {
                      entityId: v.entityId,
                      name: v.entityName,
                      description: v.description,
                      enableRegionalPerDimension: Boolean(
                        v.enableRegionalPerDimension
                      ),
                      dimensionsEnabled: Boolean(v.enableDimensions),
                      fileAttachments: [],
                      country: v.country,
                      currency: v.currency,
                      timezone: v.timezone,
                      language: v.language,
                      useForFinancialConsolidation: Boolean(
                        v.enableFinancialConsolidation
                      ),
                      enableAsFilter: Boolean(v.enableEntityFilter),
                      internalNotes: v.internalNotes,
                    };

                    // Call the update API
                    await dispatch(
                      updateEntityOverviewAction(entityData._id, payload)
                    );
                    message.success("Overview updated successfully!");

                    // Navigate to next step without refreshing
                    if (currentStep < steps.length - 1) {
                      setCurrentStep(currentStep + 1);
                    }
                  } else if (currentStep === 1) {
                    // Let FormatsS2 handle its own submission
                    await s2Ref.current?.submit?.();
                    message.success("Formats updated successfully!");

                    // Refresh formats data
                    if (entityId) {
                      await dispatch(getEntityFormatsAction(entityId));
                    }

                    // Close the drawer after FormatsS2 is completed
                    onClose();
                  }
                } catch (e) {
                  console.error("Save & Continue failed:", e);
                  message.error(e || "Failed to update entity");
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              {isRefreshing ? "Refreshing..." : "Save and Continue"}
            </Button>
          </Flex>
        </Flex>
      }
    >
      <Flex align="center" justify="space-between" style={{ padding: 16 }}>
        <Flex gap={8} wrap="wrap">
          {steps.map((s) => (
            <StepTag key={s.key} step={s} />
          ))}
        </Flex>
      </Flex>

      <div style={{ padding: 16 }}>
        {isRefreshing ? (
          <Flex
            justify="center"
            align="center"
            style={{ width: "100%", height: "100%" }}
          >
            <CustomLoaderIcon />
          </Flex>
        ) : (
          renderStepContent()
        )}
      </div>
    </Drawer>
  );
};

export default OverviewDrawerParent;
